/**
 * Tiny DOM construction helpers. Keeps screen code declarative without pulling
 * in a framework. `el` creates an element with attributes/children; children
 * may be strings, nodes, or nested arrays.
 */

type Attrs = Record<string, string | number | boolean | ((e: Event) => void)>;
type Child = string | Node | null | undefined | Child[];

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (typeof value === "function") {
      // Event handler: attr "onClick" -> "click".
      const event = key.replace(/^on/, "").toLowerCase();
      node.addEventListener(event, value as EventListener);
    } else if (key === "class") {
      node.className = String(value);
    } else if (value === true) {
      node.setAttribute(key, "");
    } else if (value !== false) {
      node.setAttribute(key, String(value));
    }
  }
  appendChildren(node, children);
  return node;
}

function appendChildren(node: Node, children: Child[]): void {
  for (const child of children) {
    if (child == null) continue;
    if (Array.isArray(child)) appendChildren(node, child);
    else if (typeof child === "string")
      node.appendChild(document.createTextNode(child));
    else node.appendChild(child);
  }
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}
